export type DevelopmentSuiteUser = {
  id: number;
  email: string;
};

export type Project = {
  id: number;
  municipality_id: number;
  name: string;
  description: string | null;
  site_address: string | null;
  parcel_identifier: string | null;
  responsible_user: DevelopmentSuiteUser | null;
  created_by: DevelopmentSuiteUser | null;
  created_at: string;
  updated_at: string;
  // Only on show/create/update responses, not in the list.
  permits_count?: number;
  planning_records_count?: number;
};

export type ProjectPayload = {
  name: string;
  description: string | null;
  site_address: string | null;
  parcel_identifier: string | null;
  responsible_user_id: number | null;
};

/** URL segment and collection key for the independent records under a Project. */
export type ProjectRecordKind = "permits" | "planning_records";

const RECORD_MEMBER_KEY: Record<ProjectRecordKind, "permit" | "planning_record"> = {
  permits: "permit",
  planning_records: "planning_record",
};

export type ProjectRecord = {
  id: number;
  project_id: number;
  municipality_id: number;
  title: string;
  external_reference: string | null;
  created_by: DevelopmentSuiteUser | null;
  created_at: string;
  updated_at: string;
};

export type ProjectRecordPayload = {
  title: string;
  external_reference: string | null;
};

export type Pagination = {
  current_page: number;
  total_pages: number;
  per_page: number;
  total_count: number;
};

/**
 * A failed suite request. `status` lets the UI tell a denied workspace (403),
 * a missing record (404), and a validation error (422, `messages` from the
 * server) apart from a failure worth retrying.
 */
export class DevelopmentSuiteRequestError extends Error {
  readonly status: number;
  readonly messages: string[];

  constructor(status: number, messages: string[], fallback: string) {
    super(messages.length > 0 ? messages.join(" ") : fallback);
    this.name = "DevelopmentSuiteRequestError";
    this.status = status;
    this.messages = messages;
  }
}

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

async function request<T>(
  path: string,
  init?: { method: "POST" | "PATCH" | "DELETE"; body?: unknown },
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: init?.method ?? "GET",
      credentials: "include",
      headers: init
        ? { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() }
        : { Accept: "application/json" },
      body: init?.body === undefined ? undefined : JSON.stringify(init.body),
    });
  } catch {
    throw new DevelopmentSuiteRequestError(0, [], "Ordinal could not be reached. Check your connection.");
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const messages: string[] = Array.isArray(body.errors)
      ? body.errors
      : typeof body.error === "string"
        ? [body.error]
        : [];
    throw new DevelopmentSuiteRequestError(response.status, messages, "Something went wrong on our side.");
  }
  // A removal answers 204 with no body.
  if (response.status === 204) return undefined as T;
  return response.json();
}

const projectsPath = (municipalityId: number) => `/api/v1/municipalities/${municipalityId}/projects`;

export function listProjects(
  municipalityId: number,
  page: number,
): Promise<{ projects: Project[]; pagination: Pagination }> {
  return request(`${projectsPath(municipalityId)}?page=${page}`);
}

export async function getProject(municipalityId: number, projectId: number): Promise<Project> {
  const body = await request<{ project: Project }>(
    `${projectsPath(municipalityId)}/${projectId}`,
  );
  return body.project;
}

export async function createProject(municipalityId: number, payload: ProjectPayload): Promise<Project> {
  const body = await request<{ project: Project }>(projectsPath(municipalityId), {
    method: "POST",
    body: { project: payload },
  });
  return body.project;
}

export async function updateProject(
  municipalityId: number,
  projectId: number,
  payload: ProjectPayload,
): Promise<Project> {
  const body = await request<{ project: Project }>(
    `${projectsPath(municipalityId)}/${projectId}`,
    { method: "PATCH", body: { project: payload } },
  );
  return body.project;
}

const recordsPath = (municipalityId: number, projectId: number, kind: ProjectRecordKind) =>
  `${projectsPath(municipalityId)}/${projectId}/${kind}`;

export async function listProjectRecords(
  municipalityId: number,
  projectId: number,
  kind: ProjectRecordKind,
  page: number,
): Promise<{ records: ProjectRecord[]; pagination: Pagination }> {
  const body = await request<Record<ProjectRecordKind, ProjectRecord[]> & { pagination: Pagination }>(
    `${recordsPath(municipalityId, projectId, kind)}?page=${page}`,
  );
  return { records: body[kind], pagination: body.pagination };
}

export async function saveProjectRecord(
  municipalityId: number,
  projectId: number,
  kind: ProjectRecordKind,
  recordId: number | null,
  payload: ProjectRecordPayload,
): Promise<ProjectRecord> {
  const memberKey = RECORD_MEMBER_KEY[kind];
  const base = recordsPath(municipalityId, projectId, kind);
  const body = await request<Record<typeof memberKey, ProjectRecord>>(
    recordId == null ? base : `${base}/${recordId}`,
    { method: recordId == null ? "POST" : "PATCH", body: { [memberKey]: payload } },
  );
  return body[memberKey];
}

export type ContactKind = "person" | "organization";

/**
 * A person or organization staff work with on a project. A contact has no
 * login and is never invited; its role lives on each ProjectContact.
 */
export type Contact = {
  id: number;
  municipality_id: number;
  kind: ContactKind;
  name: string;
  email: string | null;
  phone: string | null;
  created_at: string;
  updated_at: string;
};

export type ContactDetailsPayload = {
  name: string;
  email: string | null;
  phone: string | null;
};

export type ContactPayload = ContactDetailsPayload & { kind: ContactKind };

/** One contact's role on one project. The same contact can hold several. */
export type ProjectContact = {
  id: number;
  project_id: number;
  municipality_id: number;
  role: string;
  contact: Contact;
  created_by: DevelopmentSuiteUser | null;
  created_at: string;
  updated_at: string;
};

/** Links an existing contact from this municipality or a new one, never both. */
export type ProjectContactPayload = { role: string; contact_id: number } | { role: string; contact: ContactPayload };

const contactsPath = (municipalityId: number) => `/api/v1/municipalities/${municipalityId}/contacts`;

/**
 * The municipality's contact directory, matched on name or email for the
 * picker. The picker shows the first page only; a longer match list asks
 * the user to narrow the search.
 */
export function listContacts(
  municipalityId: number,
  query: string,
): Promise<{ contacts: Contact[]; kinds: ContactKind[]; pagination: Pagination }> {
  const params = new URLSearchParams({ page: "1" });
  if (query) params.set("q", query);
  return request(`${contactsPath(municipalityId)}?${params}`);
}

export async function updateContact(
  municipalityId: number,
  contactId: number,
  payload: ContactDetailsPayload,
): Promise<Contact> {
  const body = await request<{ contact: Contact }>(`${contactsPath(municipalityId)}/${contactId}`, {
    method: "PATCH",
    body: { contact: payload },
  });
  return body.contact;
}

const projectContactsPath = (municipalityId: number, projectId: number) =>
  `${projectsPath(municipalityId)}/${projectId}/project_contacts`;

export function listProjectContacts(
  municipalityId: number,
  projectId: number,
  page: number,
): Promise<{ project_contacts: ProjectContact[]; roles: string[]; pagination: Pagination }> {
  return request(`${projectContactsPath(municipalityId, projectId)}?page=${page}`);
}

export async function addProjectContact(
  municipalityId: number,
  projectId: number,
  payload: ProjectContactPayload,
): Promise<ProjectContact> {
  const body = await request<{ project_contact: ProjectContact }>(projectContactsPath(municipalityId, projectId), {
    method: "POST",
    body: { project_contact: payload },
  });
  return body.project_contact;
}

export async function updateProjectContactRole(
  municipalityId: number,
  projectId: number,
  projectContactId: number,
  role: string,
): Promise<ProjectContact> {
  const body = await request<{ project_contact: ProjectContact }>(
    `${projectContactsPath(municipalityId, projectId)}/${projectContactId}`,
    { method: "PATCH", body: { project_contact: { role } } },
  );
  return body.project_contact;
}

/** Removes the contact from this project only; the contact itself stays. */
export function removeProjectContact(
  municipalityId: number,
  projectId: number,
  projectContactId: number,
): Promise<void> {
  return request(`${projectContactsPath(municipalityId, projectId)}/${projectContactId}`, { method: "DELETE" });
}
